/**
 * AccountFirmsRpc: the signed-in person's firms in this region that resolve a
 * given Cova app (spec multi-product 002 §11.2, 003 §9, 007 APP-13; connected-apps-v2 019
 * WP1.2.6). A client app's bare `/` reads it, then runs `homeFor` from
 * `@cova/shell/home` over the answer: one firm ⇒ straight in, several ⇒ the
 * firm picker (a person in two firms is never put in one silently, PR-11),
 * none ⇒ the no-access page.
 *
 * ═══ WHY IN THE SHELL PACKAGE ═══
 *
 * Client apps may not import the platform's `src/` (R-001-1), and
 * `@cova/platform-contracts` (WP1.25) does not exist yet. The contract sits
 * beside `./home`, the rule that consumes it, until that package is created;
 * then it moves there unchanged. The handler stays in the platform's identity
 * context (`src/contexts/identity/presentation/account-firms-rpc-impl.ts`),
 * routed at `ACCOUNT_FIRMS_RPC_PATH`.
 *
 * ═══ RULES ═══
 *
 * - Account-level (005 API-4, `AppAccessExempt` "account"): no firm header is
 *   needed and none is read, because choosing the firm is the caller's job.
 *   A person with no firm gets an empty list, never a refusal.
 * - The person is request-derived; no payload names them. `app` only filters
 *   the list, and `lastUsedOrgId` is a hint echoed back as `lastUsed` on a
 *   firm only when the person is a member there, so a forged value can only
 *   steer the caller's own navigation.
 * - Never an authority: the firm the person lands in re-admits them live
 *   (ID-13). The list is read live on every call (nothing is stored, app-access
 *   R5): the firms whose seat resolves `app_<app>` under the firm's acting
 *   scope, the same answer the firm's own gate gives, unless `allFirms` asks
 *   for every membership (no resolution at all).
 * - The answer carries this deployment's app `origins`, so a client app can
 *   link to another app's home (the platform from Bots' no-access page, L5).
 * - The platform attaches the middleware a package cannot import
 *   (`KillSetMiddleware`) when it serves the group.
 */
import { Rpc, RpcGroup } from "@effect/rpc"
import { Schema as S } from "effect"
import { AppAccessExempt } from "@cova/agent-v2-core/contracts/access"
import { CovaApp } from "@cova/agent-v2-core/contracts/agent-products"
import { RequestIdentityMiddleware } from "@cova/agent-v2-core/contracts/middleware/request-identity-middleware"
import { defineWireError, WireErrorSanitizerMiddleware } from "@cova/agent-v2-core/contracts/wire"

/** The platform route that serves the group; relative, so a client app reaches it through its `/api/*` rewrite. */
export const ACCOUNT_FIRMS_RPC_PATH = "/api/rpc/account/firms" as const

/** APPEND-ONLY once shipped; a client branches on the code. */
export const ACCOUNT_FIRMS_WIRE_CODES = [
  /** No signed-in person on the request: the client sends them to sign-in. */
  "ACCOUNT_FIRMS_UNAUTHENTICATED",
  "ACCOUNT_FIRMS_INTERNAL",
] as const

export class AccountFirmsWireError extends defineWireError<AccountFirmsWireError>()(
  "AccountFirmsWireError",
  ACCOUNT_FIRMS_WIRE_CODES,
) {}

/** One firm the person can open `app` in. */
export const AccountFirm = S.Struct({
  orgId: S.String,
  /** The firm's name (clipped). */
  name: S.String,
  /** Null only on pre-v7.06 data. */
  slug: S.NullOr(S.String),
  /**
   * The `/o/<segment>` value: the slug, and only the slug (`homeSegmentOf`).
   * Null for a firm with no slug (pre-v7.06 data): it is not routable by
   * segment, and the org id never stands in (D46).
   */
  segment: S.NullOr(S.String),
  /** True on the one firm the caller's `lastUsedOrgId` hint named, when the person is a member there. */
  lastUsed: S.Boolean,
  /**
   * The firm's one scope (`organisations.scope`: insurance-broker, underwriter,
   * business, staff); null while unknown. Decodes to
   * null from a server that does not send it yet. Never an authority: the firm
   * the person opens re-admits them live (ID-13).
   */
  scope: S.optionalWith(S.NullOr(S.String), { default: () => null }),
})
export type AccountFirm = typeof AccountFirm.Type

export const AccountFirmsPayload = S.Struct({
  app: CovaApp,
  /** The firm this tab last worked in, when the client knows it. A hint, never trusted. */
  lastUsedOrgId: S.NullOr(S.String),
  /**
   * True lists every firm the person is a member of, whatever their seat
   * resolves; absent or false lists only the firms whose seat resolves `app`.
   * A route that resolves its firm by slug reads all of them, so an unpaid
   * firm's admin without the app's seat still opens the firm and reaches
   * Billing/Seats (021 G47 (10), D69); what they see inside stays governed by
   * each page's own checks. Still the person's memberships only: a firm they
   * are not a member of is never listed, whatever their staff role (D69).
   */
  allFirms: S.optional(S.Boolean),
})
export type AccountFirmsPayload = typeof AccountFirmsPayload.Type

/**
 * Each app's origin on the serving deployment (`appOriginsFor`); an app this
 * deployment does not offer is absent, and an unknown deployment (a local env
 * without a stage) sends none, so no client guesses another region's host.
 */
export const AccountAppOrigins = S.partial(S.Record({ key: CovaApp, value: S.String }))
export type AccountAppOrigins = typeof AccountAppOrigins.Type

export const AccountFirms = S.Struct({
  app: CovaApp,
  /**
   * The firms whose seat resolves `app` (every membership with `allFirms`), by
   * name then id. At most 25: the caller's `lastUsedOrgId` firm is always kept
   * when the person is a member there.
   */
  firms: S.Array(AccountFirm),
  /** Additive: decodes to `{}` from a server that does not send it yet. */
  origins: S.optionalWith(AccountAppOrigins, { default: () => ({}) }),
})
export type AccountFirms = typeof AccountFirms.Type

export const ListMyFirmsRpc = Rpc.make("listMyFirms", {
  payload: AccountFirmsPayload,
  success: AccountFirms,
  error: AccountFirmsWireError,
})

export class AccountFirmsRpc extends RpcGroup.make(ListMyFirmsRpc)
  .annotateRpcs(AppAccessExempt, "account")
  .middleware(RequestIdentityMiddleware)
  .middleware(WireErrorSanitizerMiddleware) {}
